import React, { useState, useEffect } from 'react';
import {
  Award, Plus, Edit2, Trash2, Check, X,
  Star, Shield, Crown, Gem
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useAdminAuth } from '../context/AdminAuthContext';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/utils';

// Feature mapping for display
const FEATURE_MAP = {
  dashboard: { name: 'Dashboard', desc: 'Main dashboard access' },
  directory: { name: 'Member Directory', desc: 'Access to member listings' },
  quotes: { name: 'Quote Tool', desc: 'Create and respond to quotes' },
  events: { name: 'Event Connect', desc: 'Event registration and meetings' },
  messaging: { name: 'Direct Messaging', desc: 'Chat with other members' },
  rewards: { name: 'Rewards Program', desc: 'Points and rewards access' },
  team: { name: 'Team Management', desc: 'Manage team members' },
  analytics: { name: 'Analytics Dashboard', desc: 'Advanced analytics' },
  ma_portal: { name: 'M&A Portal', desc: 'Mergers & acquisitions' },
  referrals: { name: 'Referral Program', desc: 'Refer new partners' },
  complaints: { name: 'Complaints', desc: 'File and track complaints' }
};

// Helper function to get features from feature_access
const getFeaturesFromAccess = (featureAccess) => {
  if (!featureAccess) return [];
  return Object.entries(FEATURE_MAP)
    .filter(([key]) => featureAccess[key] === true)
    .map(([key, value]) => value.name);
};

// Get all available features list for checkboxes
const AVAILABLE_FEATURES = Object.entries(FEATURE_MAP).map(([id, { name, desc }]) => ({
  id,
  name,
  desc
}));

const calculateMonthlyFromAnnual = (annualValue) => {
  const annual = Number(annualValue || 0);
  if (!annual || annual <= 0) return 0;
  return Math.ceil(annual / 12);
};

const formatUsd = (value) => `USD ${Number(value || 0).toLocaleString()}`;

const AdminTiers = () => {
  const [tiers, setTiers] = useState([]);
  const [enabledFeatures, setEnabledFeatures] = useState({});
  const [pointsConfig, setPointsConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tiers');

  // Dialogs
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [editingTier, setEditingTier] = useState(null);

  // Form
  const [tierForm, setTierForm] = useState({
    tier_name: '',
    display_name: '',
    price_annual: 0,
    price_monthly: 0,
    feature_access: {
      dashboard: true,
      directory: true,
      quotes: true,
      events: true,
      messaging: false,
      rewards: false,
      team: false,
      analytics: false,
      ma_portal: false,
      referrals: true,
      complaints: true
    },
    max_quotes_per_month: 10,
    max_enquiries_per_month: 10,
    max_team_members: 3,
    priority_support: false,
    ma_access: false,
    is_active: true,
    sort_order: 0
  });

  const { api, hasPermission } = useAdminAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tiersRes, pointsRes] = await Promise.all([
        api.get('/api/admin/members/tiers/config'),
        api.get('/api/admin/members/points/config')
      ]);
      setTiers(tiersRes.data.tiers || []);
      setEnabledFeatures(tiersRes.data.enabled_features || {});
      setPointsConfig({
        points_system_enabled: true,
        ...pointsRes.data
      });
    } catch (error) {
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTier = async () => {
    if (!tierForm.tier_name || !tierForm.display_name) {
      toast.error('Please fill in tier name and display name');
      return;
    }

    try {
      // Ensure disabled features stay false in feature_access
      const featureAccess = { ...tierForm.feature_access };
      Object.keys(featureAccess).forEach((key) => {
        if (enabledFeatures[key] === false) featureAccess[key] = false;
      });
      const featuresList = getFeaturesFromAccess(featureAccess);

      const tierData = {
        ...tierForm,
        feature_access: featureAccess,
        features: featuresList // Sync features array with feature_access
      };

      if (editingTier) {
        await api.put(`/api/admin/members/tiers/config/${editingTier.tier_name}`, tierData);
        toast.success('Tier updated');
      } else {
        await api.post('/api/admin/members/tiers/config', tierData);
        toast.success('Tier created');
      }
      setShowTierDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to save tier'));
    }
  };

  const handleDeleteTier = async (tierName) => {
    if (!window.confirm('Are you sure you want to delete this tier?')) return;
    try {
      await api.delete(`/api/admin/members/tiers/config/${tierName}`);
      toast.success('Tier deactivated');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to delete tier'));
    }
  };

  const handleSavePoints = async () => {
    try {
      await api.put('/api/admin/members/points/config', pointsConfig);
      toast.success('Points configuration updated');
    } catch (error) {
      toast.error('Failed to update points configuration');
    }
  };

  const resetForm = () => {
    setTierForm({
      tier_name: '',
      display_name: '',
      price_annual: 0,
      price_monthly: 0,
      feature_access: {
        dashboard: true,
        directory: true,
        quotes: true,
        events: true,
        messaging: false,
        rewards: false,
        team: false,
        analytics: false,
        ma_portal: false,
        referrals: true,
        complaints: true
      },
      max_quotes_per_month: 10,
      max_enquiries_per_month: 10,
      max_team_members: 3,
      priority_support: false,
      ma_access: false,
      is_active: true,
      sort_order: 0
    });
    setEditingTier(null);
  };

  const openEditDialog = (tier) => {
    setEditingTier(tier);
    setTierForm({
      tier_name: tier.tier_name,
      display_name: tier.display_name,
      price_annual: tier.price_annual || 0,
      price_monthly: calculateMonthlyFromAnnual(tier.price_annual || 0),
      feature_access: tier.feature_access || {
        dashboard: true,
        directory: true,
        quotes: true,
        events: true,
        messaging: false,
        rewards: false,
        team: false,
        analytics: false,
        ma_portal: false,
        referrals: true,
        complaints: true
      },
      max_quotes_per_month: tier.max_quotes_per_month || 10,
      max_enquiries_per_month: tier.max_enquiries_per_month || 10,
      max_team_members: tier.max_team_members || 3,
      priority_support: tier.priority_support || false,
      ma_access: tier.ma_access || false,
      is_active: tier.is_active !== false,
      sort_order: tier.sort_order || 0
    });
    setShowTierDialog(true);
  };

  const getTierIcon = (tierName) => {
    const icons = {
      silver: <Star size={24} className="text-gray-500" />,
      gold: <Crown size={24} className="text-amber-500" />,
      platinum: <Gem size={24} className="text-blue-500" />,
      founder: <Shield size={24} className="text-purple-500" />
    };
    return icons[tierName?.toLowerCase()] || <Award size={24} className="text-gray-500" />;
  };

  const getTierColor = (tierName) => {
    const colors = {
      silver: { bg: 'bg-gray-100', border: 'border-gray-300', text: 'text-gray-700' },
      gold: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700' },
      platinum: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700' },
      founder: { bg: 'bg-purple-50', border: 'border-purple-300', text: 'text-purple-700' }
    };
    return colors[tierName?.toLowerCase()] || colors.silver;
  };

  const handleFeatureToggle = async (featureId, enabled) => {
    try {
      await api.put('/api/admin/members/tiers/feature-flags', {
        feature_id: featureId,
        enabled
      });
      setEnabledFeatures(prev => ({ ...prev, [featureId]: enabled }));
      toast.success(enabled ? `${FEATURE_MAP[featureId]?.name || featureId} enabled` : `${FEATURE_MAP[featureId]?.name || featureId} disabled for all tiers`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to update feature'));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Configuration</h1>
          <p className="text-gray-500">Manage tiers, pricing, and points system</p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="tiers">
            <Award size={16} className="mr-2" />
            Membership Tiers
          </TabsTrigger>
          <TabsTrigger value="features">
            <Shield size={16} className="mr-2" />
            Feature Access
          </TabsTrigger>
          <TabsTrigger value="points">
            <Star size={16} className="mr-2" />
            Points System
          </TabsTrigger>
        </TabsList>

        {/* Tiers Tab */}
        <TabsContent value="tiers" className="space-y-6">
          <div className="flex justify-end">
            {hasPermission('tiers', 'create') && (
              <Button
                onClick={() => { resetForm(); setShowTierDialog(true); }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                <Plus size={16} className="mr-2" />
                Add Tier
              </Button>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {tiers.map((tier) => {
                const colors = getTierColor(tier.tier_name);
                return (
                  <Card key={tier.tier_name} className={`${colors.bg} border-2 ${colors.border}`}>
                    <div className={!tier.is_active ? 'opacity-50' : ''}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          {getTierIcon(tier.tier_name)}
                          {!tier.is_active && <Badge variant="outline">Inactive</Badge>}
                        </div>
                        <CardTitle className={`text-xl ${colors.text}`}>{tier.display_name}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4 pt-0">
                        {/* Pricing */}
                        <div>
                          <p className="text-2xl xl:text-3xl font-bold leading-tight">
                            {formatUsd(tier.price_monthly)}
                            <span className="text-lg xl:text-xl font-semibold">/month</span>
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            billed annually at {formatUsd(tier.price_annual)}/year
                          </p>
                        </div>

                        {/* Features - Display from features array in database */}
                        <div className="space-y-2">
                          {(tier.features || []).slice(0, 4).map((feature, idx) => (
                            <div key={idx} className="flex items-center text-sm">
                              <Check size={14} className="mr-2 text-green-500" />
                              {feature}
                            </div>
                          ))}
                          {(tier.features || []).length > 4 && (
                            <p className="text-xs text-gray-500">+{(tier.features || []).length - 4} more features</p>
                          )}
                        </div>

                        {/* Limits */}
                        <div className="pt-2 border-t space-y-1 text-sm text-gray-600">
                          <p>📝 {tier.max_quotes_per_month || 'Unlimited'} quotes/month</p>
                          <p>📋 {tier.max_enquiries_per_month || 'Unlimited'} enquiries/month</p>
                          <p>👥 {tier.max_team_members || 'Unlimited'} team members</p>
                          {tier.priority_support && <p>⚡ Priority support</p>}
                          {tier.ma_access && <p>🤝 M&A access</p>}
                        </div>
                      </CardContent>
                    </div>

                    {/* Actions - always full opacity */}
                    {hasPermission('tiers', 'edit') && (
                      <div className="flex space-x-2 pt-2 px-6 pb-4">
                        <Button size="sm" variant="outline" className="flex-1" onClick={() => openEditDialog(tier)}>
                          <Edit2 size={14} className="mr-1" />
                          Edit
                        </Button>
                        {hasPermission('tiers', 'delete') && (
                          <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeleteTier(tier.tier_name)}>
                            <Trash2 size={14} />
                          </Button>
                        )}
                      </div>
                    )}
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Feature Access Tab */}
        <TabsContent value="features" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Feature Access by Tier</CardTitle>
              <p className="text-sm text-gray-500">Configure which features each tier can access. Toggle features on/off globally. Disabled features are unchecked for all tiers and cannot be selected.</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium">Feature</th>
                      {tiers.map(tier => (
                        <th key={tier.tier_name} className="px-4 py-3 text-center font-medium">{tier.display_name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {[
                      { id: 'dashboard', name: 'Dashboard', desc: 'Main dashboard access' },
                      { id: 'directory', name: 'Member Directory', desc: 'Access to member listings' },
                      { id: 'quotes', name: 'Quote Tool', desc: 'Create and respond to quotes' },
                      { id: 'events', name: 'Event Connect', desc: 'Event registration and meetings' },
                      { id: 'messaging', name: 'Direct Messaging', desc: 'Chat with other members' },
                      { id: 'rewards', name: 'Rewards Program', desc: 'Points and rewards access' },
                      { id: 'team', name: 'Team Management', desc: 'Manage team members' },
                      { id: 'analytics', name: 'Analytics Dashboard', desc: 'Advanced analytics' },
                      { id: 'ma_portal', name: 'M&A Portal', desc: 'Mergers & acquisitions' },
                      { id: 'referrals', name: 'Referral Program', desc: 'Refer new partners' },
                      { id: 'complaints', name: 'Complaints', desc: 'File and track complaints' },
                    ].map(feature => {
                      const isFeatureEnabled = enabledFeatures[feature.id] !== false;
                      return (
                        <tr key={feature.id} className={`hover:bg-gray-50 ${!isFeatureEnabled ? 'bg-gray-100' : ''}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <p className={`font-medium ${!isFeatureEnabled ? 'text-gray-400' : ''}`}>{feature.name}</p>
                                <p className={`text-xs ${!isFeatureEnabled ? 'text-gray-300' : 'text-gray-500'}`}>{feature.desc}</p>
                              </div>
                              {hasPermission('tiers', 'edit') && (
                                <Switch
                                  checked={isFeatureEnabled}
                                  onCheckedChange={(checked) => handleFeatureToggle(feature.id, checked)}
                                  className="shrink-0"
                                />
                              )}
                            </div>
                          </td>
                          {tiers.map(tier => {
                            const featureAccess = tier.feature_access || {};
                            const hasAccess = featureAccess[feature.id] !== false;

                            return (
                              <td key={tier.tier_name} className={`px-4 py-3 text-center ${!isFeatureEnabled ? 'bg-gray-100' : ''}`}>
                                {hasPermission('tiers', 'edit') ? (
                                  <input
                                    type="checkbox"
                                    checked={isFeatureEnabled && hasAccess}
                                    disabled={!isFeatureEnabled}
                                    onChange={async (e) => {
                                      if (!isFeatureEnabled) return;
                                      const newFeatureAccess = {
                                        ...tier.feature_access,
                                        [feature.id]: e.target.checked
                                      };
                                      try {
                                        await api.put(`/api/admin/members/tiers/config/${tier.tier_name}`, {
                                          feature_access: newFeatureAccess
                                        });
                                        toast.success(`Updated ${feature.name} access for ${tier.display_name}`);
                                        fetchData();
                                      } catch (error) {
                                        toast.error('Failed to update feature access');
                                      }
                                    }}
                                    className={`w-5 h-5 accent-green-600 ${isFeatureEnabled ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                                  />
                                ) : (
                                  isFeatureEnabled && hasAccess ? (
                                    <Check size={18} className="mx-auto text-green-600" />
                                  ) : (
                                    <X size={18} className={`mx-auto ${isFeatureEnabled ? 'text-gray-300' : 'text-gray-200'}`} />
                                  )
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Tip:</strong> Members with disabled features will see a lock icon and upgrade prompt when attempting to access them.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Points Tab */}
        <TabsContent value="points" className="space-y-6">
          {pointsConfig && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <Card className="col-span-2">
                  <CardHeader>
                    <CardTitle>Points System</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                      <div>
                        <p className="font-medium text-gray-900">Points system enabled</p>
                        <p className="text-sm text-gray-500">
                          Turn off all point awards, deductions, redemptions, referral credits, and admin grants.
                        </p>
                      </div>
                      <Switch
                        checked={pointsConfig.points_system_enabled !== false}
                        onCheckedChange={(checked) => setPointsConfig({ ...pointsConfig, points_system_enabled: checked })}
                      />
                    </div>
                  </CardContent>
                </Card>
                {/* Points Earning */}
                <Card>
                  <CardHeader>
                    <CardTitle>Points Earning Activities</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Deal Won Bonus</Label>
                      <Input
                        type="number"
                        value={pointsConfig.deal_won_bonus || 50}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, deal_won_bonus: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Points awarded when a deal is successfully closed</p>
                    </div>
                    <div>
                      <Label>Signup Bonus</Label>
                      <Input
                        type="number"
                        value={pointsConfig.signup_bonus}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, signup_bonus: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Referral Bonus</Label>
                      <Input
                        type="number"
                        value={pointsConfig.referral_bonus}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, referral_bonus: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Quote Completion Bonus</Label>
                      <Input
                        type="number"
                        value={pointsConfig.quote_completion_bonus}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, quote_completion_bonus: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Event Attendance Bonus</Label>
                      <Input
                        type="number"
                        value={pointsConfig.event_attendance_bonus}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, event_attendance_bonus: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Points Deductions */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-red-600">Points Deductions</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Complaint Received Deduction</Label>
                      <Input
                        type="number"
                        value={pointsConfig.complaint_deduction || 25}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, complaint_deduction: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Points deducted when a valid complaint is filed against member</p>
                    </div>
                    <div>
                      <Label>Late Payment Deduction</Label>
                      <Input
                        type="number"
                        value={pointsConfig.late_payment_deduction || 10}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, late_payment_deduction: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Cancelled Quote Deduction</Label>
                      <Input
                        type="number"
                        value={pointsConfig.cancelled_quote_deduction || 5}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, cancelled_quote_deduction: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Points Redemption Rules */}
              <Card>
                <CardHeader>
                  <CardTitle>Points Redemption Rules</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <Label>Promoted Quote Cost (points/day)</Label>
                      <Input
                        type="number"
                        value={pointsConfig.promoted_quote_cost || 12}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, promoted_quote_cost: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Cost to promote a quote to the top</p>
                    </div>
                    <div>
                      <Label>Priority Quote Cost</Label>
                      <Input
                        type="number"
                        value={pointsConfig.priority_quote_cost || 100}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, priority_quote_cost: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                      <p className="text-xs text-gray-500 mt-1">Cost for medium priority quotes</p>
                    </div>
                    <div>
                      <Label>High Priority Quote Cost</Label>
                      <Input
                        type="number"
                        value={pointsConfig.high_priority_quote_cost || 250}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, high_priority_quote_cost: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Max Points for Event/Sponsorship (%)</Label>
                      <Input
                        type="number"
                        value={pointsConfig.max_points_redemption_percent || 15}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, max_points_redemption_percent: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                        min="0"
                        max="100"
                      />
                      <p className="text-xs text-gray-500 mt-1">Max % of event/sponsorship cost payable via points</p>
                    </div>
                    <div>
                      <Label>Points Expiry (Days)</Label>
                      <Input
                        type="number"
                        value={pointsConfig.points_expiry_days || ''}
                        onChange={(e) => setPointsConfig({ ...pointsConfig, points_expiry_days: e.target.value ? parseInt(e.target.value) : null })}
                        placeholder="Leave empty for no expiry"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tier Multipliers & Discounts */}
              <Card>
                <CardHeader>
                  <CardTitle>Tier Multipliers & Discounts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left">Tier</th>
                          <th className="px-4 py-2 text-center">Points Multiplier</th>
                          <th className="px-4 py-2 text-center">Points Discount (%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {['Silver', 'Gold', 'Platinum', 'Rhodium', 'Titanium', 'Founder'].map((tier) => (
                          <tr key={tier} className="border-b">
                            <td className="px-4 py-3 font-medium">{tier}</td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center space-x-2">
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={pointsConfig.tier_multipliers?.[tier] || 1}
                                  onChange={(e) => setPointsConfig({
                                    ...pointsConfig,
                                    tier_multipliers: {
                                      ...pointsConfig.tier_multipliers,
                                      [tier]: parseFloat(e.target.value) || 1
                                    }
                                  })}
                                  className="w-20 text-center"
                                />
                                <span className="text-gray-500">x</span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center space-x-2">
                                <Input
                                  type="number"
                                  value={pointsConfig.tier_discounts?.[tier] || (tier === 'Founder' ? 50 : tier === 'Titanium' ? 40 : tier === 'Rhodium' ? 30 : tier === 'Platinum' ? 20 : tier === 'Gold' ? 10 : 0)}
                                  onChange={(e) => setPointsConfig({
                                    ...pointsConfig,
                                    tier_discounts: {
                                      ...pointsConfig.tier_discounts,
                                      [tier]: parseInt(e.target.value) || 0
                                    }
                                  })}
                                  className="w-20 text-center"
                                  min="0"
                                  max="100"
                                />
                                <span className="text-gray-500">%</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <div>
                {hasPermission('settings', 'edit') && (
                  <Button onClick={handleSavePoints} className="bg-green-600 hover:bg-green-700 text-white">
                    <Check size={16} className="mr-2" />
                    Save Points Configuration
                  </Button>
                )}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Tier Dialog */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTier ? 'Edit Tier' : 'Create New Tier'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tier Name (ID) *</Label>
                <Input
                  value={tierForm.tier_name}
                  onChange={(e) => setTierForm({ ...tierForm, tier_name: e.target.value.toLowerCase() })}
                  placeholder="e.g., enterprise"
                  disabled={!!editingTier}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Display Name *</Label>
                <Input
                  value={tierForm.display_name}
                  onChange={(e) => setTierForm({ ...tierForm, display_name: e.target.value })}
                  placeholder="e.g., Enterprise"
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Monthly Price (USD / month)</Label>
                <Input
                  type="number"
                  value={tierForm.price_monthly}
                  readOnly
                  disabled
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">Auto-calculated from annual price (rounded up).</p>
              </div>
              <div>
                <Label>Annual Price (USD / year)</Label>
                <Input
                  type="number"
                  value={tierForm.price_annual}
                  onChange={(e) => {
                    const annualValue = parseFloat(e.target.value) || 0;
                    setTierForm({
                      ...tierForm,
                      price_annual: annualValue,
                      price_monthly: calculateMonthlyFromAnnual(annualValue)
                    });
                  }}
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Max Quotes/Month (0=Unlimited)</Label>
                <Input
                  type="number"
                  value={tierForm.max_quotes_per_month}
                  onChange={(e) => setTierForm({ ...tierForm, max_quotes_per_month: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">0 = Unlimited</p>
              </div>
              <div>
                <Label>Max Enquiries/Month</Label>
                <Input
                  type="number"
                  value={tierForm.max_enquiries_per_month || 0}
                  onChange={(e) => setTierForm({ ...tierForm, max_enquiries_per_month: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">0 = Unlimited</p>
              </div>
              <div>
                <Label>Max Team Members</Label>
                <Input
                  type="number"
                  value={tierForm.max_team_members}
                  onChange={(e) => setTierForm({ ...tierForm, max_team_members: parseInt(e.target.value) || 0 })}
                  className="mt-1"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">0 = Unlimited</p>
              </div>
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tierForm.priority_support}
                  onChange={(e) => setTierForm({ ...tierForm, priority_support: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Priority Support</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tierForm.ma_access}
                  onChange={(e) => setTierForm({ ...tierForm, ma_access: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>M&A Access</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={tierForm.is_active}
                  onChange={(e) => setTierForm({ ...tierForm, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <span>Active</span>
              </label>
            </div>

            <div>
              <Label>Feature Access</Label>
              <p className="text-xs text-gray-500 mb-3">Select which features are available for this tier (disabled features are hidden)</p>
              <div className="space-y-2 mt-2 max-h-64 overflow-y-auto border rounded-lg p-3">
                {AVAILABLE_FEATURES.filter((f) => enabledFeatures[f.id] !== false).map((feature) => {
                  const hasAccess = tierForm.feature_access[feature.id] !== false;
                  return (
                    <label
                      key={feature.id}
                      className="flex items-start space-x-3 p-2 hover:bg-gray-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={hasAccess}
                        onChange={(e) => {
                          setTierForm({
                            ...tierForm,
                            feature_access: {
                              ...tierForm.feature_access,
                              [feature.id]: e.target.checked
                            }
                          });
                        }}
                        className="mt-1 w-4 h-4 cursor-pointer accent-green-600"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{feature.name}</p>
                        <p className="text-xs text-gray-500">{feature.desc}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowTierDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveTier} className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingTier ? 'Update Tier' : 'Create Tier'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTiers;
